import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Sparkles, ShieldAlert, BookOpenText, FileText, BarChart3,
  Building2, Bell, Settings, Search, ChevronDown, LogOut, Command, Plus, Compass, User, Plug
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { GlowBackdrop } from "@/components/primitives";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/app/copilot", label: "AI Copilot", icon: Sparkles, id: "copilot" },
  { to: "/app/incidents", label: "Incident Intelligence", icon: ShieldAlert, id: "incidents" },
  { to: "/app/knowledge", label: "Knowledge Base", icon: BookOpenText, id: "knowledge" },
  { to: "/app/documents", label: "Document Intel", icon: FileText, id: "documents" },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, id: "analytics" },
  { to: "/app/organization", label: "Organization", icon: Building2, id: "organization" },
  { to: "/app/connectors", label: "Connectors", icon: Plug, id: "connectors" },
  { to: "/app/notifications", label: "Notifications", icon: Bell, id: "notifications" },
  { to: "/app/settings", label: "Settings", icon: Settings, id: "settings" },
];

function Sidebar({ workspace, setWorkspace }) {
  return (
    <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-64 z-40 flex-col gap-2 p-4 glass-soft border-r border-white/5 bg-[#050816]/85" data-testid="app-sidebar">
      <Link to="/app/dashboard" className="flex items-center gap-2 px-2 pt-1 pb-3" data-testid="sidebar-logo">
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-cyan-400 via-blue-500 to-violet-500 flex items-center justify-center shadow-[0_0_20px_rgba(0,240,255,0.4)]">
          <Sparkles className="h-4 w-4 text-white" />
        </div>
        <div className="leading-tight">
          <div className="text-sm font-bold tracking-tight">SentinelAI</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Ops Copilot</div>
        </div>
      </Link>

      <button
        data-testid="sidebar-workspace-selector"
        onClick={() => setWorkspace(workspace === "Production" ? "Staging" : "Production")}
        className="flex items-center justify-between gap-2 px-3 py-2 mt-1 mb-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/15 hover:bg-white/[0.06] transition"
      >
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-cyan-500/40 to-violet-500/40 grid place-items-center text-[10px] font-bold">SA</div>
          <div className="text-left leading-tight">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Workspace</div>
            <div className="text-sm font-semibold">{workspace}</div>
          </div>
        </div>
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>

      <nav className="flex flex-col gap-1">
        {NAV.map((n) => (
          <NavLink
            key={n.id}
            to={n.to}
            data-testid={`nav-${n.id}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition border ${
                isActive
                  ? "bg-gradient-to-r from-cyan-500/10 to-violet-500/10 border-white/10 text-white shadow-[0_0_24px_rgba(0,240,255,0.08)]"
                  : "border-transparent text-slate-400 hover:text-white hover:bg-white/[0.04]"
              }`
            }
          >
            <n.icon className="h-4 w-4" />
            <span className="font-medium">{n.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto rounded-2xl glass p-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">System Pulse</div>
        <div className="flex items-end gap-1 h-10">
          {[6, 9, 5, 11, 7, 12, 8, 14, 10, 13].map((h, i) => (
            <div key={i} className="w-1.5 rounded-full bg-gradient-to-t from-cyan-500/40 to-violet-500/40" style={{ height: `${h * 6}%` }} />
          ))}
        </div>
        <div className="text-xs text-slate-400 mt-2">All 14 services <span className="text-emerald-400">healthy</span></div>
        <div className="mt-3 pt-3 border-t border-white/5 text-[10px] font-mono uppercase tracking-widest text-slate-500">
          Built by <span className="gradient-text font-bold">Team Hack</span>
          <div className="mt-0.5 text-[10px] normal-case tracking-normal text-slate-400 font-sans">Anu · Aravind · Narendar · Sahil</div>
        </div>
      </div>
    </aside>
  );
}

function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [palette, setPalette] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((p) => !p);
      }
      if (e.key === "Escape") setPalette(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <header className="sticky top-0 z-30 h-16 backdrop-blur-2xl bg-[#050816]/70 border-b border-white/5 flex items-center justify-between px-4 lg:px-8" data-testid="app-navbar">
        <button
          onClick={() => setPalette(true)}
          data-testid="global-search-trigger"
          className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-slate-400 hover:text-white transition w-72 justify-between"
        >
          <span className="flex items-center gap-2 text-sm"><Search className="h-4 w-4" /> Search anything…</span>
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/5 border border-white/10">⌘K</span>
        </button>

        <div className="flex items-center gap-2">
          <button data-testid="navbar-notifications" onClick={() => navigate("/app/notifications")} className="relative p-2 rounded-full hover:bg-white/10 text-slate-300">
            <Bell className="h-4 w-4" />
            <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-cyan-400" />
          </button>
          <button data-testid="navbar-quick-create" onClick={() => navigate("/app/incidents")} className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-sm">
            <Plus className="h-3.5 w-3.5" /> New Incident
          </button>
          <div className="relative">
            <button data-testid="navbar-user-menu" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 px-2 py-1.5 rounded-full hover:bg-white/[0.06] border border-transparent hover:border-white/10">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-cyan-400 to-violet-500 grid place-items-center text-xs font-bold">{(user?.name || "U")[0]}</div>
              <div className="hidden md:flex flex-col items-start leading-tight">
                <span className="text-sm font-medium">{user?.name}</span>
                <span className="text-[10px] uppercase tracking-widest text-slate-500">{user?.role}</span>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>
            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  className="absolute right-0 mt-2 w-56 glass p-2 z-50"
                >
                  <div className="px-3 py-2 text-xs text-slate-400 border-b border-white/5">{user?.email}</div>
                  <button data-testid="user-menu-profile" onClick={() => { setOpen(false); navigate("/app/settings"); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 flex items-center gap-2 text-sm"><User className="h-4 w-4" /> Profile</button>
                  <button data-testid="user-menu-settings" onClick={() => { setOpen(false); navigate("/app/settings"); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 flex items-center gap-2 text-sm"><Settings className="h-4 w-4" /> Settings</button>
                  <button data-testid="user-menu-logout" onClick={logout} className="w-full text-left px-3 py-2 rounded-lg hover:bg-red-500/10 text-red-300 flex items-center gap-2 text-sm"><LogOut className="h-4 w-4" /> Sign out</button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {palette && <CommandPalette onClose={() => setPalette(false)} />}
      </AnimatePresence>
    </>
  );
}

function CommandPalette({ onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const items = [
    ...NAV.map((n) => ({ label: n.label, hint: "Page", to: n.to, icon: n.icon })),
    { label: "Generate Incident RCA", hint: "AI Action", to: "/app/incidents", icon: Sparkles },
    { label: "Ask Knowledge Base", hint: "AI Action", to: "/app/knowledge", icon: Compass },
    { label: "Upload Document", hint: "Action", to: "/app/documents", icon: FileText },
  ].filter((i) => !q || i.label.toLowerCase().includes(q.toLowerCase()));
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center pt-28"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: -10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10 }}
        onClick={(e) => e.stopPropagation()}
        className="w-[640px] max-w-[92vw] glass p-3"
        data-testid="command-palette"
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
          <Command className="h-4 w-4 text-cyan-300" />
          <input
            autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="command-palette-input"
            placeholder="Search pages, run AI actions…"
            className="bg-transparent outline-none flex-1 text-sm placeholder:text-slate-500"
          />
          <span className="text-[10px] font-mono text-slate-500">ESC to close</span>
        </div>
        <div className="max-h-80 overflow-auto py-2">
          {items.map((it, i) => (
            <button
              key={i}
              onClick={() => { onClose(); navigate(it.to); }}
              className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-white/5 text-left"
            >
              <span className="flex items-center gap-2 text-sm"><it.icon className="h-4 w-4 text-cyan-300" /> {it.label}</span>
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{it.hint}</span>
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function AppLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [workspace, setWorkspace] = useState("Production");

  useEffect(() => {
    if (!user && !localStorage.getItem("sentinel_token")) navigate("/login");
  }, [user, navigate]);

  return (
    <div className="min-h-screen relative">
      <GlowBackdrop />
      <Sidebar workspace={workspace} setWorkspace={setWorkspace} />
      <div className="lg:pl-64">
        <Navbar />
        <main className="px-4 lg:px-8 py-8">
          <AnimatePresence mode="wait">
            <motion.div key={location.pathname}>
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
